import { DataMappingWebSocket } from "./dataMappingWebSocket";
import { buildGenerateMethodologyPrompt } from "../components/generateMethodologyPrompt";

// How long to wait for the agent's reply before giving up. Generous: this is a real LLM turn (same one
// the visible chat uses), not a classification call — see data-sorting/CLAUDE.md's model-selection notes
// on adaptive thinking latency. Bounded well inside the 180s ALB idle timeout the whole service sits
// behind (iac/esg-fastapi/index.ts).
const REPLY_TIMEOUT_MS = 90000;

/**
 * Ask the agent to write a methodology statement for this session, over a WebSocket connection dedicated
 * to this one request — separate from the connection `useDataMappingWebSocket` opens for the visible
 * ChatPanel.
 *
 * Why a second connection rather than sending through the existing one: the backend has no concept of a
 * "silent" turn (`display` changes what a transcript SHOWS, it never hides a turn — see
 * data-sorting/CLAUDE.md's "The chat is only ever a conversation" section, and `useDataMappingWebSocket.js`
 * pushes every send/response straight into `chatStore`, which is what renders the visible transcript). A
 * second connection is a normal turn on the SAME session (same agent, same persisted history — the backend
 * keys chat history by session id, not by connection), whose events this function reads for itself instead
 * of handing to `chatStore` — nothing appears in ChatPanel while it runs.
 *
 * The turn is also sent `ephemeral`, so the backend answers it without saving it: it never lands in
 * `ds_session_message`, so reopening the session's chat does not show a "Writing a methodology
 * statement…" turn followed by the statement itself, and the model's history does not gain a copy of every
 * draft. The agent still READS the session's history for this turn, which is what makes the statement
 * describe what actually happened.
 *
 * Resolves with the plain-text/Markdown reply. Rejects on a connection failure, an agent error, or timeout.
 */
export function generateMethodologyViaChat(sessionId, { target } = {}) {
  return new Promise((resolve, reject) => {
    const { text, display } = buildGenerateMethodologyPrompt({ target });
    let settled = false;
    let timer = null;

    const finish = (fn, value) => {
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      ws.disconnect();
      fn(value);
    };

    const ws = new DataMappingWebSocket(
      sessionId,
      (msg) => {
        // Ignore streaming chrome (thinking/token/tool_*/data_modified/token_usage) — this caller only
        // wants the finished text, same as any other one-shot request. `token_usage` is not persisted by
        // this call either; the backend records it against the session regardless of how the turn was
        // sent, exactly as it does for the visible chat.
        if (msg.type === "response") {
          finish(resolve, typeof msg.data === "string" ? msg.data : String(msg.data ?? ""));
        } else if (msg.type === "error") {
          finish(reject, new Error(typeof msg.data === "string" ? msg.data : "The agent could not generate a methodology."));
        }
      },
      (connected, detail) => {
        if (!connected && detail?.reason) {
          finish(reject, new Error(detail.reason));
        }
      }
    );

    timer = setTimeout(() => {
      finish(reject, new Error("Timed out waiting for a reply."));
    }, REPLY_TIMEOUT_MS);

    ws.connect();

    const trySend = () => {
      if (settled) return;
      if (ws.isOpen) {
        // `quiet: true` — this connection has no "not connected" banner to show; a failed send here
        // surfaces through the timeout instead.
        if (!ws.send(text, { display, quiet: true, ephemeral: true })) {
          finish(reject, new Error("Could not send the request."));
        }
        return;
      }
      setTimeout(trySend, 100);
    };
    trySend();
  });
}

export default generateMethodologyViaChat;
